import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { defaultTicketTypeConfig, TicketTypeConfig } from './TicketTypeConfig';
import { ticketEasyApi } from '../../../ticketEasy-api';
import { TicketApprover } from '../ticketApprover/TicketApprover';
interface TicketTypeConfigState {
    ticketTypeConfig: TicketTypeConfig;
    ticketTypeConfigList: TicketTypeConfig[];
    loading: boolean;
    error: string | null;
}

const initialState: TicketTypeConfigState = {
    ticketTypeConfig: defaultTicketTypeConfig(),
    ticketTypeConfigList: [],
    loading: false,
    error: null,
};

const ticketTypeConfigSlice = createSlice({
    name: 'ticketTypeConfig',
    initialState,
    reducers: {
        setTicketTypeConfig(state, action: PayloadAction<TicketTypeConfig>) {
            state.ticketTypeConfig = action.payload;
        },
        resetTicketTypeConfig(state) {
            state.ticketTypeConfig = defaultTicketTypeConfig();
        },
        setTicketTypeConfigList(state, action: PayloadAction<TicketTypeConfig[]>) {
            state.ticketTypeConfigList = action.payload;
        },
        addTicketTypeConfig(state, action: PayloadAction<TicketTypeConfig>) {
            state.ticketTypeConfigList.push(action.payload);
        },
        addJobTitleMapId(state, action: PayloadAction<string>) {
            state.ticketTypeConfig.jobTitleMapIds.push(action.payload);
        },
        removeJobTitleMapId(state, action: PayloadAction<string>) {
            state.ticketTypeConfig.jobTitleMapIds = state.ticketTypeConfig.jobTitleMapIds.filter((id) => id !== action.payload);
        },
        addApprover(state, action: PayloadAction<TicketApprover>) {
            state.ticketTypeConfig.approvers.push(action.payload);
        },
        removeApprover(state, action: PayloadAction<string>) {
            state.ticketTypeConfig.approvers = state.ticketTypeConfig.approvers.filter((approver) => approver.profileId !== action.payload);
        },
        addThirdPartyEmail(state, action: PayloadAction<string>) {
            if (!state.ticketTypeConfig.thirdPartyEmails.includes(action.payload)) {
                state.ticketTypeConfig.thirdPartyEmails.push(action.payload);
            }
        },
        removeThirdPartyEmail(state, action: PayloadAction<string>) {
            state.ticketTypeConfig.thirdPartyEmails = state.ticketTypeConfig.thirdPartyEmails.filter(email => email !== action.payload);
        },

    },
    extraReducers: (builder) => {
        builder
            .addMatcher(
                ticketEasyApi.endpoints.ticketTypeConfigPost.matchFulfilled,
                (state, action) => {
                    // Since the API returns void, get the data from the original command
                    const ticketTypeConfig = action.meta.arg.originalArgs.message;
                    state.ticketTypeConfig = ticketTypeConfig;
                    state.ticketTypeConfigList.push(ticketTypeConfig);
                    state.loading = false;
                    state.error = null;
                }
            )
            .addMatcher(
                ticketEasyApi.endpoints.ticketTypeConfigPost.matchPending,
                (state) => {
                    state.loading = true;
                    state.error = null;
                }
            )
            .addMatcher(
                ticketEasyApi.endpoints.ticketTypeConfigPost.matchRejected,
                (state, action) => {
                    state.loading = false;
                    state.error = action.error.message || 'Failed to post ticket type config';
                }
            )
            .addMatcher(
                ticketEasyApi.endpoints.ticketTypeConfig.matchFulfilled,
                (state, action: PayloadAction<TicketTypeConfig>) => {
                    state.ticketTypeConfig = action.payload;
                    state.loading = false;
                    state.error = null;
                }
            )
            .addMatcher(
                ticketEasyApi.endpoints.ticketTypeConfig.matchPending,
                (state) => {
                    state.loading = true;
                    state.error = null;
                }
            )
            .addMatcher(
                ticketEasyApi.endpoints.ticketTypeConfig.matchRejected,
                (state, action) => {
                    state.loading = false;
                    state.error = action.error.message || 'Failed to fetch ticket type config';
                }
            )
            .addMatcher(
                ticketEasyApi.endpoints.ticketTypeConfigSearch.matchFulfilled,
                (state, action: PayloadAction<TicketTypeConfig[]>) => {
                    state.ticketTypeConfigList = action.payload;
                    state.loading = false;
                    state.error = null;
                }
            )
            .addMatcher(
                ticketEasyApi.endpoints.ticketTypeConfigSearch.matchPending,
                (state) => {
                    state.loading = true;
                    state.error = null;
                }
            )
            .addMatcher(
                ticketEasyApi.endpoints.ticketTypeConfigSearch.matchRejected,
                (state, action) => {
                    state.loading = false;
                    state.error = action.error.message || 'Failed to fetch ticket type config list';
                }
            );
    },
});

export const { setTicketTypeConfig, resetTicketTypeConfig, setTicketTypeConfigList, addTicketTypeConfig, addJobTitleMapId
    , removeJobTitleMapId, addApprover, removeApprover, addThirdPartyEmail, removeThirdPartyEmail
 } = ticketTypeConfigSlice.actions;

export const handleApproverSubmit = (approver: TicketApprover) => (dispatch: any) => {
    dispatch(addApprover(approver));
};

export default ticketTypeConfigSlice.reducer;